import {
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
} from '@angular/core';

import { environment } from './environments/environment';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { TokenInterceptor } from '@modules/shared/interceptors/token.interceptor';
import { ErrorInterceptor } from '@modules/errors/interceptors/error.interceptor';
import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { Store, StoreModule } from '@ngrx/store';
import { AppReducer } from '@modules/shared/store';
import { EffectsModule } from '@ngrx/effects';
import { AuthorizationEffects } from '@modules/authorization/store/authorization.effects';
import { CommonEffects } from '@modules/shared/store/common.effects';
import { ProposalEffects } from '@modules/order/store/proposal/proposal.effects';
import { DynamicFormEffects } from '@modules/dynamic-form/store/dynamicForm.effects';
import { ContractsEffects } from '@modules/order/store/contracts/contracts.effects';
import { ScoringEffects } from '@modules/scoring/store/scoring.effects';
import { AgreementsEffects } from '@modules/agreements/store/agreements.effects';
import { ConditionsEffects } from '@modules/conditions/store/conditions.effects';
import { PaymentEffects } from '@modules/payment/store/payment.effects';
import { StatusEffects } from '@modules/status/store/status.effects';
import { UserProfileEffects } from '@modules/user-profile/store/userProfile.effects';
import { DocumentsEffects } from '@modules/documents/store/documents.effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NgxSpinnerModule } from 'ngx-spinner';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import localeRu from '@angular/common/locales/ru';
import { AppComponent } from './app/app.component';
import { provideRouter } from '@angular/router';
import { routes } from './app/app.routes';
import { EventEffects } from '@modules/order/store/event/event.effects';
import { AuthorizationCheck } from '@modules/authorization/store/authorization.action';
import { GlobalErrorHandler } from '@modules/shared/services';

if (environment.production) {
  enableProdMode();
}

registerLocaleData(localeRu);

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(routes),
    importProvidersFrom(
      CommonModule,
      BrowserModule,
      StoreModule.forRoot(AppReducer, {}),
      EffectsModule.forRoot([
        AuthorizationEffects,
        CommonEffects,
        ProposalEffects,
        DynamicFormEffects,
        ContractsEffects,
        EventEffects,
        ScoringEffects,
        AgreementsEffects,
        ConditionsEffects,
        PaymentEffects,
        StatusEffects,
        UserProfileEffects,
        DocumentsEffects,
      ]),
      StoreDevtoolsModule.instrument({ maxAge: 25, connectInZone: true }),
      NgxMaskDirective,
      NgxSpinnerModule.forRoot({ type: 'ball-scale-multiple' }),
      MatProgressSpinnerModule
    ),
    DatePipe,
    provideNgxMask(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    { provide: LOCALE_ID, useValue: 'ru' },
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(initializeFingerprintAndCheckAuth),
  ],
}).catch(err => console.error(err));

async function initializeFingerprintAndCheckAuth(): Promise<void> {
  const store = inject(Store);

  if (localStorage.getItem('deviceUid')) {
    store.dispatch(new AuthorizationCheck());
    return;
  }

  const fp = await FingerprintJS.load();
  const result = await fp.get();

  if (!localStorage.getItem('deviceUid')) {
    localStorage.setItem('deviceUid', result.visitorId);
  }
  store.dispatch(new AuthorizationCheck());
}
